import { useMemo } from 'react';
import type { Behavior, JobTypeVersionContent, TransformChainStep } from '@capture/shared';
import { findCrossFieldOps, normalizeTransforms, type CrossFieldOffender } from '@capture/shared';

export interface OtherDestinationSummary {
  destinationId: string;
  destinationName: string;
  /** For inline / legacy transforms: the count. For named refs: 0 (we don't fetch). */
  chainStepCount: number;
  /** True when the destination's transforms is { kind: 'named', namedChainId }. */
  isNamedRef: boolean;
}

export interface PromoteToCanonicalContext {
  existingPostOcr: Behavior[];
  otherDestinations: OtherDestinationSummary[];
  crossFieldOffenders: CrossFieldOffender[];
  isAllowed: boolean;
}

interface Args {
  content: JobTypeVersionContent;
  sourceFieldName: string;
  sourceDestinationId: string;
  resolvedSteps: TransformChainStep[];
}

export function usePromoteToCanonicalContext({
  content,
  sourceFieldName,
  sourceDestinationId,
  resolvedSteps,
}: Args): PromoteToCanonicalContext {
  return useMemo(() => {
    const field = content.fields.find((f) => f.name === sourceFieldName);
    const existingPostOcr = field?.stageMap['post-ocr'] ?? [];

    const otherDestinations: OtherDestinationSummary[] = [];
    for (const dest of content.export.destinations) {
      if (dest.id === sourceDestinationId) continue;
      const mapping = dest.mappings.find((m) => m.sourceField === sourceFieldName);
      if (!mapping) continue;
      const raw = mapping.transforms;
      const isNamedRef =
        raw !== undefined && raw !== null && !Array.isArray(raw) &&
        typeof raw === 'object' && (raw as { kind?: string }).kind === 'named';
      let stepCount = 0;
      if (!isNamedRef) {
        const normalized = normalizeTransforms(raw);
        if (normalized.kind === 'inline') {
          stepCount = normalized.steps.length;
        }
      }
      otherDestinations.push({
        destinationId: dest.id,
        destinationName: dest.name,
        chainStepCount: stepCount,
        isNamedRef,
      });
    }

    const crossFieldOffenders = findCrossFieldOps(resolvedSteps);
    const isAllowed = crossFieldOffenders.length === 0;

    return { existingPostOcr, otherDestinations, crossFieldOffenders, isAllowed };
  }, [content, sourceFieldName, sourceDestinationId, resolvedSteps]);
}
